/*
 * <steals-cards> — carrossel de cards editoriais (clone HBX).
 * Sem setas e sem barrinha (como na HBX). Navegacao:
 *   - swipe/trackpad no scroll nativo (funciona sem este script);
 *   - arrasto com o mouse (grab-and-drag) sobre o scroll nativo;
 *   - autoplay: 1 card por intervalo, loop infinito.
 * Pausas do autoplay: durante o arrasto, hover no track, aba oculta, carrossel
 * fora do viewport (<50%) e prefers-reduced-motion (autoplay nunca liga).
 * Progressive enhancement: sem este script o scroll nativo continua e o
 * autoplay/arrasto simplesmente nao acontecem.
 */
class StealsCards extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.steals-cards__track');
    if (!this.track) return;

    this.interval = (parseFloat(this.getAttribute('data-autoplay-interval')) || 5) * 1000;
    this.activeIndex = 0;
    this._timer = null;
    this._hover = false;
    this._inView = false;
    this._dragging = false;
    this._rmq = window.matchMedia('(prefers-reduced-motion: reduce)');

    this._ac = new AbortController();
    const { signal } = this._ac;

    this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true, signal });

    // Hover no track pausa o autoplay; sair retoma
    this.track.addEventListener('mouseenter', () => { this._hover = true; this.pause(); }, { signal });
    this.track.addEventListener('mouseleave', () => { this._hover = false; this.resume(); }, { signal });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pause();
      else this.resume();
    }, { signal });

    this._rmq.addEventListener('change', () => {
      if (this._rmq.matches) this.pause();
      else this.resume();
    }, { signal });

    // Autoplay so roda com o carrossel visivel (>= 50% no viewport)
    this._io = new IntersectionObserver((entries) => {
      this._inView = entries[entries.length - 1].intersectionRatio >= 0.5;
      if (this._inView) this.resume();
      else this.pause();
    }, { threshold: [0, 0.5, 1] });
    this._io.observe(this);

    this._resizeObserver = new ResizeObserver(() => this.queueUpdate());
    this._resizeObserver.observe(this.track);

    this.setupDrag(signal);
    this.queueUpdate();
  }

  disconnectedCallback() {
    if (this._ac) this._ac.abort();
    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this._io) this._io.disconnect();
    if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
    this.pause();
  }

  /*
   * Arrasto com o mouse (grab-and-drag) sobre o scroll nativo. So mouse/caneta
   * — touch e trackpad ja rolam nativamente. Durante o arrasto o scroll-snap e
   * desligado pra nao brigar com o scrollLeft manual; ao soltar, o snap volta e
   * encaixa no card mais proximo. Se houve arrasto de verdade (> threshold), o
   * clique seguinte e cancelado pra nao abrir o link do card.
   */
  setupDrag(signal) {
    let startX = 0;
    let startLeft = 0;
    let down = false;
    let dragging = false;
    let pointerId = null;

    // Vira arrasto de verdade: SO agora captura o ponteiro e desliga o snap.
    // Capturar no pointerdown redirecionaria o evento `click` de compatibilidade
    // pro track (elemento capturador) — e como o track e ANCESTRAL do <a> do
    // card, o link nunca receberia o clique e nao navegaria (bug so no desktop;
    // no touch o onDown retorna antes). Por isso a captura fica aqui.
    const beginDrag = () => {
      dragging = true;
      this._dragging = true;
      this.track.classList.add('is-dragging');
      try { this.track.setPointerCapture(pointerId); } catch (_) { /* noop */ }
      this.pause();
    };

    const onDown = (e) => {
      if (e.pointerType === 'touch' || e.button !== 0) return;
      down = true;
      dragging = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startLeft = this.track.scrollLeft;
      // nao captura aqui — senao quebra o clique no card (ver beginDrag)
    };

    const onMove = (e) => {
      if (!down) return;
      if (e.buttons === 0) { down = false; return; } // botao soltou fora do track
      const dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) <= 3) return; // ainda pode ser um clique, nao arrasto
        beginDrag();
      }
      this.track.scrollLeft = startLeft - dx;
    };

    const onUp = () => {
      if (!down) return;
      down = false;
      if (!dragging) return; // clique puro: nao capturou nada, o link navega normal
      dragging = false;
      this._dragging = false;
      try { this.track.releasePointerCapture(pointerId); } catch (_) { /* noop */ }
      // alinha no card mais proximo com o snap ainda desligado, depois reativa
      // o snap ja na posicao certa (sem "pulo")
      this.snapToNearest();
      this.track.classList.remove('is-dragging');
      // cancela o clique que vem logo apos o arrasto (nao abre o card)
      const cancelClick = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
      this.addEventListener('click', cancelClick, { capture: true, once: true });
      // se nenhum clique vier, remove o guard no proximo tick
      setTimeout(() => this.removeEventListener('click', cancelClick, { capture: true }), 0);
      this.resume();
    };

    this.track.addEventListener('pointerdown', onDown, { signal });
    this.track.addEventListener('pointermove', onMove, { signal });
    this.track.addEventListener('pointerup', onUp, { signal });
    this.track.addEventListener('pointercancel', onUp, { signal });
  }

  // Encaixa no card mais proximo de forma instantanea (roda com o snap ainda
  // desligado; ao reativar .is-dragging o snap-mandatory ja acha tudo alinhado)
  snapToNearest() {
    const width = this.cardWidth();
    if (width <= 0) return;
    const index = Math.round(this.track.scrollLeft / width);
    this.track.scrollLeft = index * width;
  }

  autoplayWanted() {
    return this.hasAttribute('data-autoplay')
      && !this._rmq.matches
      && this.cardCount() > 1;
  }

  canAutoplay() {
    return this.autoplayWanted()
      && this._inView
      && !this._hover
      && !this._dragging
      && !document.hidden
      && this.track.scrollWidth > this.track.clientWidth + 1;
  }

  cardCount() {
    return this.track.querySelectorAll('.steals-cards__card').length;
  }

  cardWidth() {
    const card = this.track.querySelector('.steals-cards__card');
    if (!card) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  maxScroll() {
    return this.track.scrollWidth - this.track.clientWidth;
  }

  scrollBehavior() {
    return this._rmq.matches ? 'auto' : 'smooth';
  }

  next() {
    const atEnd = this.track.scrollLeft >= this.maxScroll() - 1;
    if (atEnd) {
      // ultimo card: volta pro inicio (loop infinito, como na HBX)
      this.track.scrollTo({ left: 0, behavior: this.scrollBehavior() });
    } else {
      this.track.scrollTo({
        left: (this.activeIndex + 1) * this.cardWidth(),
        behavior: this.scrollBehavior(),
      });
    }
  }

  pause() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  resume() {
    this.restart();
  }

  // Rearma o timer do card atual do zero
  restart() {
    this.pause();
    if (this.canAutoplay()) {
      this._timer = setTimeout(() => {
        this._timer = null;
        this.next();
      }, this.interval);
    }
  }

  queueUpdate() {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = null;
      this.update();
    });
  }

  update() {
    // scrollLeft math assumes LTR layout (site is pt-BR only).
    const t = this.track;
    const hasOverflow = t.scrollWidth > t.clientWidth + 1;
    this.classList.toggle('is-scrollable', hasOverflow);

    const width = this.cardWidth();
    let index = width > 0 ? Math.round(t.scrollLeft / width) : 0;
    // encostou no fim: com cards fracionarios visiveis o ultimo card nunca vira
    // o mais a esquerda, entao o fim do track conta como ultimo indice
    const count = this.cardCount();
    if (hasOverflow && t.scrollLeft >= this.maxScroll() - 1) index = count - 1;
    index = Math.max(0, Math.min(index, count - 1));

    if (index !== this.activeIndex) {
      this.activeIndex = index;
      // mudanca de card (autoplay OU manual) reinicia o timer — senao o slide
      // troca no meio da leitura
      this.restart();
    }
  }
}

if (!customElements.get('steals-cards')) {
  customElements.define('steals-cards', StealsCards);
}
